import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useStyles } from './styles';
import { Fade, Grid2 } from '@mui/material';
import Card from '../Card';
import BarChart from '../BarChart';
import LineChart from '../LineChart';
import Container from '../Container';
import { useTraffic } from 'core/contexts/traffic';
import { CircularIndeterminate } from '@utriper/core-ui';

const TrafficReport = () => {
    const { t: translate } = useTranslation();
    const classes = useStyles();
    const { loading, visitsTable, visitDaily } = useTraffic();

    // Normalize visitsTable data
    const normalizedVisitsTable = useMemo(() => {
        if (Array.isArray(visitsTable)) return visitsTable;
        if (typeof visitsTable === 'object' && visitsTable !== null) {
            return Object.entries(visitsTable).map(([key, value]) => ({
                key,
                ...value,
            }));
        }
        return [];
    }, [visitsTable]);

    // Calculate totals for Cards
    const trafficTotals = useMemo(() => {
        if (normalizedVisitsTable.length === 0) return [];

        const totalVisits = normalizedVisitsTable.reduce((acc, row) => acc + (Number(row.totalVisits) || 0), 0);
        const totalUniqueVisits = normalizedVisitsTable.reduce((acc, row) => acc + (Number(row.uniqueVisits) || 0), 0);
        const totalConverted = normalizedVisitsTable.reduce((acc, row) => acc + (Number(row.converted) || 0), 0);
        const totalMoneyConverted = normalizedVisitsTable.reduce((acc, row) => acc + (Number(row.totalMoneyConverted) || 0), 0);

        // Calculate average conversion rate
        const validRows = normalizedVisitsTable.filter((row) => typeof row.conversionRate === 'number' && !isNaN(row.conversionRate));
        const avgConversionRate = validRows.length > 0 ? validRows.reduce((acc, row) => acc + row.conversionRate, 0) / validRows.length : 0;

        return [
            { title: translate('traffic.totals.totalVisits') || 'Total Visits', value: totalVisits },
            { title: translate('traffic.totals.uniqueVisits') || 'Unique Visits', value: totalUniqueVisits },
            { title: translate('traffic.totals.totalConverted') || 'Total Converted', value: totalConverted },
            { title: translate('traffic.totals.totalMoneyConverted') || 'Total Money Converted', value: totalMoneyConverted.toFixed(2), currency: 'USD' },
            { title: translate('traffic.totals.avgConversionRate') || 'Avg Conversion Rate', value: `${avgConversionRate.toFixed(2)}%` },
        ];
    }, [normalizedVisitsTable, translate]);

    // Prepare data for uniqueVisits bar chart
    const uniqueVisitsData = useMemo(() => {
        if (normalizedVisitsTable.length === 0) return { series: [], categories: [] };

        return {
            series: [
                {
                    name: translate('traffic.visitsTable.uniqueVisits') || 'Unique Visits',
                    data: normalizedVisitsTable.map((row) => Number(row.uniqueVisits) || 0),
                },
            ],
            categories: normalizedVisitsTable.map((row) => String(row.code || row.contractId)),
        };
    }, [normalizedVisitsTable, translate]);

    // Prepare data for totalMoneyConverted bar chart
    const moneyConvertedData = useMemo(() => {
        if (normalizedVisitsTable.length === 0) return { series: [], categories: [] };

        return {
            series: [
                {
                    name: translate('traffic.visitsTable.totalMoneyConverted') || 'Money Converted',
                    data: normalizedVisitsTable.map((row) => Number(row.totalMoneyConverted) || 0),
                },
            ],
            categories: normalizedVisitsTable.map((row) => String(row.code || row.contractId)),
        };
    }, [normalizedVisitsTable, translate]);

    // Prepare data for conversionRate bar chart
    const conversionRateData = useMemo(() => {
        if (normalizedVisitsTable.length === 0) return { series: [], categories: [] };

        return {
            series: [
                {
                    name: translate('traffic.visitsTable.conversionRate') || 'Conversion Rate',
                    data: normalizedVisitsTable.map((row) => Number(row.conversionRate) || 0),
                },
            ],
            categories: normalizedVisitsTable.map((row) => String(row.code || row.contractId)),
        };
    }, [normalizedVisitsTable, translate]);

    // Prepare data for line chart (visitDaily)
    const dailyVisitsLineData = useMemo(() => {
        if (!visitDaily || Object.keys(visitDaily).length === 0) return { series: [], categories: [] };

        const dates = Object.keys(visitDaily).sort((a, b) => new Date(a) - new Date(b));

        // Format dates as dd/mm/yyyy
        const formattedDates = dates.map((date) => {
            const d = new Date(date);
            const day = String(d.getDate()).padStart(2, '0');
            const month = String(d.getMonth() + 1).padStart(2, '0');
            const year = d.getFullYear();
            return `${day}/${month}/${year}`;
        });

        const totalUniqueVisitsData = dates.map((date) => Number(visitDaily[date]?.totalUniqueVisits) || 0);
        const totalMoneyMadeData = dates.map((date) => Number(visitDaily[date]?.totalMoneyMade) || 0);

        return {
            series: [
                {
                    name: translate('traffic.visitDaily.totalUniqueVisits') || 'Total Unique Visits',
                    data: totalUniqueVisitsData,
                },
                {
                    name: translate('traffic.visitDaily.totalMoneyMade') || 'Total Money Made',
                    data: totalMoneyMadeData,
                },
            ],
            categories: formattedDates,
        };
    }, [visitDaily, translate]);

    return loading ? (
        <div style={{ marginTop: 2 }}>
            <CircularIndeterminate />
        </div>
    ) : (
        <Fade in={!loading}>
            <Grid2 container size={12} direction="column" width="100%" justifyContent="flex-start" alignItems="flex-start" gap="16px" pb={4}>
                {/* Cards Totals */}
                <Grid2 container size={{ xs: 6, md: 12 }} gap="8px" justifyContent="flex-start" alignItems="center" width="100%" flexWrap={{ xs: 'wrap', lg: 'nowrap' }}>
                    {trafficTotals?.map(({ title, value, currency = null }) => (
                        <Card key={title} title={title} value={value} currency={currency} />
                    ))}
                </Grid2>

                {/* Three Bar Charts in One Row */}
                <Container>
                    {/* Unique Visits by Contract */}
                    {uniqueVisitsData.series.length > 0 && (
                        <Grid2 size={{ xs: 12, md: 4 }} className={classes.chartContainer}>
                            <BarChart
                                title={translate('traffic.charts.uniqueVisits') || 'Unique Visits by Contract'}
                                subtitle={translate('traffic.charts.visits') || 'Visits'}
                                label={translate('traffic.charts.visits') || 'Visits'}
                                series={uniqueVisitsData.series}
                                categories={uniqueVisitsData.categories}
                                color="#137dc5"
                            />
                        </Grid2>
                    )}

                    {/* Total Money Converted by Contract */}
                    {moneyConvertedData.series.length > 0 && (
                        <Grid2 size={{ xs: 12, md: 4 }} className={classes.chartContainer}>
                            <BarChart
                                title={translate('traffic.charts.moneyConverted') || 'Money Converted by Contract'}
                                subtitle="USD"
                                label="USD"
                                series={moneyConvertedData.series}
                                categories={moneyConvertedData.categories}
                                color="#13c56d"
                            />
                        </Grid2>
                    )}

                    {/* Conversion Rate by Contract */}
                    {conversionRateData.series.length > 0 && (
                        <Grid2 size={{ xs: 12, md: 4 }} className={classes.chartContainer}>
                            <BarChart
                                title={translate('traffic.charts.conversionRate') || 'Conversion Rate by Contract'}
                                subtitle="%"
                                label="%"
                                series={conversionRateData.series}
                                categories={conversionRateData.categories}
                                color="#c5138e"
                            />
                        </Grid2>
                    )}
                </Container>

                {/* Line Chart - Daily Visits and Money Made */}
                <Container>
                    {dailyVisitsLineData.series.length > 0 && (
                        <Grid2 size={12} className={classes.chartContainer}>
                            <LineChart
                                title={translate('traffic.charts.dailyTrends') || 'Daily Traffic Trends'}
                                subtitle={translate('traffic.charts.visitsAndRevenue') || 'Unique Visits & Revenue Over Time'}
                                series={dailyVisitsLineData.series}
                                categories={dailyVisitsLineData.categories}
                                colors={['#137dc5', '#13c56d']}
                            />
                        </Grid2>
                    )}
                </Container>
            </Grid2>
        </Fade>
    );
};

export default TrafficReport;
